// src/components/Auth/AuthPage.jsx    
import React, { useState, useEffect, useCallback, useMemo } from 'react';      
import LoginForm from './LoginForm';      
import RegisterForm from './RegisterForm';      
import ResetPasswordForm from './ResetPasswordForm';  
import EmailConfirmation from './EmailConfirmation';
import Footer from '../Footer';
import '../../auth-styles.css';

const AuthPage = React.memo(({ onAuthSuccess, defaultView = 'login' }) => {        
  const [activeView, setActiveView] = useState(defaultView);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // Check if we should show email confirmation
  useEffect(() => {
    // Check URL params for confirmation state
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('confirmation') === 'true') {
      const email = localStorage.getItem('registrationEmail');
      if (email) {
        setRegisteredEmail(email);
        setActiveView('email-confirmation');
      }
      // Clean up URL
      window.history.replaceState({}, document.title, '/auth');
    }
    
    // Also check sessionStorage for backwards compatibility
    const justRegistered = sessionStorage.getItem('justRegistered');
    const sessionEmail = sessionStorage.getItem('registeredEmail');
    
    if (justRegistered === 'true' && sessionEmail) {
      setActiveView('email-confirmation');
      setRegisteredEmail(sessionEmail);
    }
  }, []);
            
  const handleLoginSuccess = useCallback(() => {        
    if (onAuthSuccess) onAuthSuccess();        
  }, [onAuthSuccess]);        
            
  const handleRegisterSuccess = useCallback((email) => {
    // Store email in localStorage for persistence across page reload
    localStorage.setItem('registrationEmail', email);
    // Also store in sessionStorage for backwards compatibility
    sessionStorage.setItem('justRegistered', 'true');
    sessionStorage.setItem('registeredEmail', email);
    setRegisteredEmail(email);
    setActiveView('email-confirmation');        
  }, []);      
  
  const handleContinueToLogin = useCallback(() => {
    // Clear all storage flags
    localStorage.removeItem('registrationEmail');
    sessionStorage.removeItem('justRegistered');
    sessionStorage.removeItem('registeredEmail');
    setRegisteredEmail('');
    setActiveView('login');
  }, []);

  const handleSetActiveView = useCallback((view) => {
    setActiveView(view);
  }, []);
          
  const renderAuthForm = useCallback(() => {      
    switch (activeView) {      
      case 'register':      
        return (      
          <RegisterForm       
            onSuccess={handleRegisterSuccess}       
            onLoginClick={() => handleSetActiveView('login')}      
          />      
        );      
      case 'reset-password':      
        return (      
          <ResetPasswordForm       
            onLoginClick={() => handleSetActiveView('login')}      
          />      
        );
      case 'email-confirmation':
        return (
          <EmailConfirmation
            email={registeredEmail}
            onContinue={handleContinueToLogin}
          />
        );
      case 'login':      
      default:      
        return (      
          <LoginForm       
            onSuccess={handleLoginSuccess}       
            onRegisterClick={() => handleSetActiveView('register')}      
            onForgotPasswordClick={() => handleSetActiveView('reset-password')}      
          />      
        );      
    }      
  }, [activeView, registeredEmail, handleLoginSuccess, handleRegisterSuccess, handleContinueToLogin, handleSetActiveView]);

  // Memoize the logo SVG since it never changes
  const logoSvg = useMemo(() => (
    <svg  
      xmlns="http://www.w3.org/2000/svg"  
      xmlnsXlink="http://www.w3.org/1999/xlink"  
      width="40"  
      height="40"  
      viewBox="0 0 375 374.999991"  
      preserveAspectRatio="xMidYMid meet"  
      version="1.0"  
    >  
      <defs>  
        <clipPath id="d43c2f7aa6">  
          <path d="M 121 96 L 214 96 L 214 370.679688 L 121 370.679688 Z M 121 96" clipRule="nonzero"/>  
        </clipPath>  
        <clipPath id="d933478d10">  
          <path d="M 2.859375 187 L 95 187 L 95 370.679688 L 2.859375 370.679688 Z M 2.859375 187" clipRule="nonzero"/>  
        </clipPath>  
        <clipPath id="35f2660f6b">  
          <path d="M 240 4.679688 L 332 4.679688 L 332 370.679688 L 240 370.679688 Z M 240 4.679688" clipRule="nonzero"/>  
        </clipPath>  
        <clipPath id="eaa2a6214c">  
          <path d="M 49 25.917969 L 371 25.917969 L 371 348 L 49 348 Z M 49 25.917969" clipRule="nonzero"/>  
        </clipPath>  
      </defs>  
      <g clipPath="url(#d43c2f7aa6)">  
        <path fill="#14182a" d="M 194.820312 96.089844 L 139.976562 96.089844 C 129.882812 96.089844 121.691406 104.277344 121.691406 114.371094 L 121.691406 352.035156 C 121.691406 362.125 129.882812 370.316406 139.976562 370.316406 L 194.820312 370.316406 C 204.914062 370.316406 213.101562 362.125 213.101562 352.035156 L 213.101562 114.371094 C 213.101562 104.277344 204.914062 96.089844 194.820312 96.089844 Z M 194.820312 96.089844" fillOpacity="1" fillRule="nonzero"/>  
      </g>  
      <g clipPath="url(#d933478d10)">  
        <path fill="#14182a" d="M 75.988281 187.5 L 21.144531 187.5 C 11.050781 187.5 2.859375 195.6875 2.859375 205.78125 L 2.859375 352.035156 C 2.859375 362.125 11.050781 370.316406 21.144531 370.316406 L 75.988281 370.316406 C 86.082031 370.316406 94.269531 362.125 94.269531 352.035156 L 94.269531 205.78125 C 94.269531 195.6875 86.082031 187.5 75.988281 187.5 Z M 75.988281 187.5" fillOpacity="1" fillRule="nonzero"/>  
      </g>  
      <g clipPath="url(#35f2660f6b)">  
        <path fill="#14182a" d="M 313.652344 4.679688 L 258.808594 4.679688 C 248.714844 4.679688 240.523438 12.871094 240.523438 22.960938 L 240.523438 352.035156 C 240.523438 362.125 248.714844 370.316406 258.808594 370.316406 L 313.652344 370.316406 C 323.742188 370.316406 331.933594 362.125 331.933594 352.035156 L 331.933594 22.960938 C 331.933594 12.871094 323.742188 4.679688 313.652344 4.679688 Z M 313.652344 4.679688" fillOpacity="1" fillRule="nonzero"/>  
      </g>  
      <g clipPath="url(#eaa2a6214c)">  
        <path fill="#bdb4e2" d="M 256.6875 262.226562 L 335.839844 341.351562 C 343.675781 349.183594 356.402344 349.183594 364.238281 341.34375 C 372.074219 333.507812 372.070312 320.78125 364.234375 312.941406 L 285.136719 233.875 C 301.046875 212.230469 310.453125 185.503906 310.453125 156.597656 C 310.453125 84.472656 251.898438 25.917969 179.777344 25.917969 C 107.652344 25.917969 49.097656 84.472656 49.097656 156.597656 C 49.097656 228.71875 107.652344 287.273438 179.777344 287.273438 C 208.519531 287.273438 235.101562 277.976562 256.6875 262.226562 Z M 179.777344 66.085938 C 229.730469 66.085938 270.292969 106.640625 270.292969 156.597656 C 270.292969 206.550781 229.730469 247.113281 179.777344 247.113281 C 129.820312 247.113281 89.265625 206.550781 89.265625 156.597656 C 89.265625 106.640625 129.820312 66.085938 179.777344 66.085938 Z M 179.777344 66.085938" fillOpacity="1" fillRule="evenodd"/>  
      </g>  
    </svg>
  ), []);

  return (      
    <div className="min-h-screen flex flex-col bg-sws-light-cream">
      {/* Main content area with flex-1 to push footer down */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-12">      
        <div className={`w-full ${activeView === 'register' ? 'max-w-xl' : 'max-w-md'}`}>      
          {/* Logo and App Name with brand colors */}      
          <div className="text-center mb-10">      
            <div className="inline-block p-3 bg-sws-purple bg-opacity-10 rounded-full mb-4">      
              {logoSvg}  
            </div>      
            <h1 className="text-3xl font-bold text-sws-navy">Profit Clarity</h1>      
            <p className="text-sws-slate mt-2">Gain clarity on your true business profitability</p>      
          </div>

          {/* Conditionally wrap in white container based on active view */}
          {activeView === 'register' ? (
            // For register, render the form directly without white wrapper
            renderAuthForm()
          ) : activeView === 'email-confirmation' ? (
            // For email confirmation, use white wrapper
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">      
              {renderAuthForm()}      
            </div>
          ) : (
            // For all other views, keep the white wrapper
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">      
              {renderAuthForm()}      
            </div>
          )}
                  
          {/* Feature Highlights - Only show when not on email confirmation */}
          {activeView !== 'email-confirmation' && (      
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">      
              <div className="text-center">      
                <div className="rounded-full bg-sws-purple bg-opacity-20 w-12 h-12 flex items-center justify-center mx-auto mb-4">      
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-sws-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">      
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />      
                  </svg>      
                </div>      
                <h3 className="font-medium text-sws-navy">Profit Analysis</h3>      
                <p className="text-sm text-sws-slate mt-1">Understand your true costs and profitability</p>      
              </div>      
                      
              <div className="text-center">      
                <div className="rounded-full bg-sws-purple bg-opacity-20 w-12 h-12 flex items-center justify-center mx-auto mb-4">      
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-sws-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">      
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />      
                  </svg>      
                </div>      
                <h3 className="font-medium text-sws-navy">Data-Driven Insights</h3>      
                <p className="text-sm text-sws-slate mt-1">Make informed strategic decisions</p>      
              </div>      
                      
              <div className="text-center">      
                <div className="rounded-full bg-sws-purple bg-opacity-20 w-12 h-12 flex items-center justify-center mx-auto mb-4">      
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-sws-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">      
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />      
                  </svg>      
                </div>      
                <h3 className="font-medium text-sws-navy">Save Time</h3>      
                <p className="text-sm text-sws-slate mt-1">Automate profitability tracking</p>      
              </div>      
            </div>
          )}      
        </div>
      </div>
      
      {/* Footer - now outside the centered content container */}
      <Footer />         
    </div>      
  );      
});

AuthPage.displayName = 'AuthPage';

export default AuthPage;